import type { DealMessage, MessageChannel, RentalMessage } from '@/types'

/**
 * One message, whichever store it came from.
 *
 * A deal and a rental keep their messages in different tables with different
 * columns, and the panel used to be written twice because of it — the deal one
 * rendered attachments, images, event cards and read ticks, the rental one
 * rendered a paragraph of text. The same conversation therefore looked like
 * two different products depending on which page you opened it from, and a
 * photo sent during the deal vanished into a filename after the contract was
 * signed.
 *
 * So both are narrowed to this shape at the edge and there is one renderer.
 * Fields a store does not have stay undefined rather than being faked: a
 * rental row carries no read state, and showing an unticked message would
 * claim the tenant has not seen something nobody knows either way about.
 */
export interface ConversationMessage {
	id: string
	channel: MessageChannel
	direction: 'INBOUND' | 'OUTBOUND'
	content: string
	sentAt: string
	/** Deal messages only; undefined means the store does not track it. */
	isRead?: boolean
	attachmentUrl?: string
	attachmentName?: string
	attachmentSize?: number
}

export function fromDealMessage(m: DealMessage): ConversationMessage {
	return {
		id: m.id,
		channel: m.channel,
		direction: m.direction,
		content: m.content,
		sentAt: m.sentAt,
		isRead: m.isRead,
		attachmentUrl: m.attachmentUrl,
		attachmentName: m.attachmentName,
		attachmentSize: m.attachmentSize,
	}
}

/**
 * Null for a row the panel cannot place: an unnamed channel belongs to no tab
 * and an unnamed direction to no side of the thread. Guessing would put a
 * tenant's message on the landlord's side, so it is left out instead.
 */
export function fromRentalMessage(m: RentalMessage): ConversationMessage | null {
	if (m.channel === 'UNSPECIFIED' || m.direction === 'UNSPECIFIED') return null
	return {
		id: m.id,
		channel: m.channel,
		direction: m.direction,
		content: m.content,
		sentAt: m.sentAt,
		// Present on inherited deal rows (their id IS the deal message id), so
		// the rental page can acknowledge inbound messages; a rental's own log
		// keeps it undefined — no read state exists to claim.
		isRead: m.isRead,
		attachmentUrl: m.attachmentUrl,
		attachmentName: m.attachmentName,
		attachmentSize: m.attachmentSize,
	}
}
